import React, { useState, useEffect } from 'react'

import { IconPlus, IconAddItem, IconEdit } from 'components/ui/icons'
import { Button, ButtonGroup } from 'components/ui/button'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'
import { SectionHeader } from 'components/ui/section-header'
import { StatusSummary } from 'components/ui/status-summary'

import { SharedActionCreators, sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore, GettyAppState } from 'domains/org/getty/store'
import { ValidationFailuresList } from 'domains/org/getty/components/publishing/validationsList'
import { ErrorsList } from 'domains/org/getty/components/publishing/errorsList'
import { ENVIRONMENT, appEnvironment } from 'domains/org/getty/utils/environment'

import { Page } from 'generated/mos/pagination'
import { ListMoodsRequest } from 'generated/mos/org/getty/audioaccess'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { bffEndpoint } from 'helpers/bff'
import { statusSelector, Status } from 'helpers/status'
import { pick, pluralise, isNotNullOrUndefined } from 'helpers/core'
import { space } from 'helpers/style'
import { baseDeepLinkDomain, gettyCenterSiteId, gettyVillaSiteId } from 'helpers/deep-links'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { MoodCreate } from './mood-create'
import { MoodCard } from './mood-card'
import { getSubnav } from '../subnav'

import styled from 'styled'

// TODO duplicated from edit-project/index.tsx
const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

const MoodCardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: ${space(-3)};
  margin-right: ${space(-3)};
`;

type ActionProps =
  Pick<GettyActionCreators, 'gettyListMoodsRequest'| 'gettyDeleteMoodReset' | 'gettyListValidationFailuresRequest'> &
  Pick<SharedActionCreators, 'toastNotification'>

type ConnectedProps = Pick<GettyAppState, 'moods' | 'moodDelete' | 'validationFailuresList' >

type DirectProps = {
  projectId: string;
  historyPush: (id: string) => void;
  navBackRoute?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

const MoodsPageView = (props: Props) => {
  const {
    projectId,
    gettyListMoodsRequest,
    toastNotification,
    moodDelete,
    gettyDeleteMoodReset,
    validationFailuresList,
    moods,
    gettyListValidationFailuresRequest,
  } = props

  const [ moodCreate, setMoodCreate ] = useState(false)

  const data = statusSelector.data(props.moods)
  const validationFailuresListData = statusSelector.data(validationFailuresList)
  const moodsCount = data ? data.length : 0

  const hostName = bffEndpoint(window.location.hostname)

  const gettyCenterDeepLinkURL = `${baseDeepLinkDomain}?deeplink=site/${gettyCenterSiteId}/mood`;
  const gettyVillaDeepLinkURL = `${baseDeepLinkDomain}?deeplink=site/${gettyVillaSiteId}/mood`;

  useEffect(() => {
    gettyListMoodsRequest({
      ...ListMoodsRequest.defaults,
    })

    if (moodDelete.status === Status.Complete) {
      toastNotification({
        type: 'success',
        text: 'Mood journey deleted successfully',
      })
      gettyDeleteMoodReset()
    }
  }, [gettyListMoodsRequest, projectId, toastNotification, moodDelete]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (moods.status === Status.Ready) {
      const entityRefs = moods.data.map(({ ref }) => ref).filter(isNotNullOrUndefined)
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs,
        }
      })
    }
  }, [gettyListValidationFailuresRequest, moods]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LayoutListOrFormPage
      nav={() => (
        <PrimaryNavigationEditEntity
          title="Edit Project"
          navBackRoute={props.navBackRoute}
          darkTheme
        />
      )}
      title="GettyGuide"
      subnav={() => (
        <>
          <ErrorsList projectId={projectId} />
          <SecondaryNavigation navItems={getSubnav(projectId)}>
            <Button variant="subtle" onClick={() => props.historyPush(`/project/${projectId}/visitor-types`)}>
              <IconEdit size="small" />manage visitor types
            </Button>
            <Button variant="subtle" onClick={() => setMoodCreate(true)}>
              <IconPlus size="small" />create mood journey
            </Button>
          </SecondaryNavigation>
        </>
      )}
      toolbar={() => (
        <ButtonGroup>
          <ValidationFailuresList projectId={projectId} />
          <Button appearance="primary" onClick={() => props.historyPush(`/project/${projectId}/publishing`)}>
            Publish
          </Button>
        </ButtonGroup>
      )}
      content={() => (
        <>
          {moodCreate &&
            <MoodCreate
              historyPush={props.historyPush}
              onClose={() => {
                setMoodCreate(false)
              }}
              onSave={() => {
                setMoodCreate(false)
              }}
              projectId={projectId}
            />
          }

          <StatusGuard status={moods}>
            <SectionHeader heading={`${moodsCount} ${pluralise(moodsCount, "mood journey", "mood journeys")}`} />
            <MoodCardGrid>
              {data && data.map(mood => (
                <MoodCard
                  mood={mood}
                  projectId={props.projectId}
                  key={mood.ref && mood.ref.id}
                  hasWarning={validationFailuresListData && !!validationFailuresListData.find(({ ref }: ValidationFailure.Entity) => ref && mood.ref && ref.id === mood.ref.id)}
                  hostName={hostName}
                />
              ))}
            </MoodCardGrid>
          </StatusGuard>
          <>
            <h4>Deep Link - Mood Journey selector wheel</h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column'}}>
              <div>Getty Center - <a href={gettyCenterDeepLinkURL} target="_blank" rel="noopener noreferrer">{gettyCenterDeepLinkURL}</a></div>
              <br />
              <div>Getty Villa - <a href={gettyVillaDeepLinkURL} target="_blank" rel="noopener noreferrer">{gettyVillaDeepLinkURL}</a></div>
            </div>
          </>
        </>
      )}
    />
  )
}

export const MoodsPage = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'moods', 'moodDelete', 'validationFailuresList'),
  }),
  {
    ...pick(actionCreators, 'gettyListMoodsRequest', 'gettyDeleteMoodReset', 'gettyListValidationFailuresRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(MoodsPageView)
